import React, { useState } from "react";
import { DEFAULT_TAG_EMOJI, DEFAULT_TAG_PALETTE, TAG_PALETTES } from "@lifeat/shared/task-management/TaskTagConstants";
import { ValidateTagRequestResponse } from "@lifeat/shared/task-management/TaskTagUtil";
import { AmplitudeService } from "@analytics/Amplitude/AmplitudeService";
import { LifeAtButtonV2 } from "@common/LifeAtButtonV2/LifeAtButtonV2";
import { HorizontalDivider } from "@common/LifeAtDivider/LifeAtDivider";
import { useToastListContext } from "@contexts/ToastListContext";
import Styles from "@themes/Styles";
import { darkTheme } from "@themes/Theme";
import { CreateTagEvent } from "../AmplitudeTaskTagEvents";
import * as TaskListConstants from "../TaskListConstants";
import { useTaskListContext } from "../TaskListContext";
import { translateInputValidationError } from "../TaskManagementUtil";
import { TaskTagService } from "../TaskTagService";
import { TagActionButtonRow, TagMenuFooter } from "./TagDetailMenu";
import TagDetailMenuContent from "./TagDetailMenuContent";

const CREATE_TAG_LABEL = "Create";

interface TagCreationMenuProps {
    readonly validateTagName: (tagName: string) => ValidateTagRequestResponse;
    readonly handleClosePopup: () => void;
}
export default function TagCreationMenu(props: TagCreationMenuProps) {
    const amplitudeService = AmplitudeService.getInstance();
    const { addToastNotification } = useToastListContext();
    const { tags } = useTaskListContext();

    const [tagNameErrorMessage, setTagNameErrorMessage] = useState<string | undefined>();

    const [tagEmojiInputValue, setTagEmojiInputValue] = useState(DEFAULT_TAG_EMOJI);
    const [isTagDefault, setIsTagDefault] = useState(false);
    const [tagColorInputValue, setTagColorInputValue] = useState<(typeof TAG_PALETTES)[number]["primary"]>(
        DEFAULT_TAG_PALETTE.primary,
    );
    const [tagNameInputValue, setTagNameInputValue] = useState("");

    const [isCreateLoading, setIsCreateLoading] = useState(false);

    const handleValidateAndUpdateTagName = (updatedTagName: string) => {
        // if tag name validation fails, display the error message
        // and continue to update input field state
        const validateTagNameResponse = props.validateTagName(updatedTagName);
        if (validateTagNameResponse.isValid === false) {
            setTagNameErrorMessage(translateInputValidationError(validateTagNameResponse.reason, updatedTagName));
        } else {
            setTagNameErrorMessage(undefined);
        }

        setTagNameInputValue(updatedTagName);
    };

    const handleCreateTag = () => {
        setIsCreateLoading(true);

        const createTagProperties = {
            name: tagNameInputValue,
            emoji: tagEmojiInputValue,
            color: tagColorInputValue,
            isTagDefault: isTagDefault,
        };

        amplitudeService.logEvent(new CreateTagEvent({ numTags: tags.length, ...createTagProperties }));

        TaskTagService.createNewTag(createTagProperties)
            .then(() => {
                props.handleClosePopup();
            })
            .catch(() => {
                addToastNotification(
                    TaskListConstants.TAG_CREATION_ERROR_TITLE,
                    TaskListConstants.ERROR_TRY_AGAIN_MESSAGE,
                );
            });
    };

    return (
        <div
            onClick={(e) => {
                e.stopPropagation();
            }}
        >
            <TagDetailMenuContent
                tagEmojiInputValue={tagEmojiInputValue}
                isTagDefault={isTagDefault}
                tagColorInputValue={tagColorInputValue}
                tagNameInputValue={tagNameInputValue}
                handleUpdateEmoji={(updatedEmoji: string) => setTagEmojiInputValue(updatedEmoji)}
                handleUpdateColor={(updatedColor: (typeof TAG_PALETTES)[number]["primary"]) =>
                    setTagColorInputValue(updatedColor)
                }
                handleUpdateDefault={(isTagDefault: boolean) => setIsTagDefault(isTagDefault)}
                handleUpdateTagName={handleValidateAndUpdateTagName}
                tagNameErrorMessage={tagNameErrorMessage}
            />

            <TagMenuFooter>
                <HorizontalDivider widthCssValue={"100%"} />
                <TagActionButtonRow>
                    <LifeAtButtonV2
                        onClick={() => handleCreateTag()}
                        isDisabled={!props.validateTagName(tagNameInputValue).isValid}
                        variant={"solid"}
                        overrideBackgroundColor={Styles.buttonFillBlue}
                        overrideTextColor={darkTheme.textColorPrimary}
                        disableHover={true}
                        isLoading={isCreateLoading}
                    >
                        {CREATE_TAG_LABEL}
                    </LifeAtButtonV2>
                </TagActionButtonRow>
            </TagMenuFooter>
        </div>
    );
}
